import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import clsx from "clsx";
import TButton from "@/components/atoms/buttons/TButton";
import HtmlText from "../common/HtmlText";
import icon, { IIconName } from "@/utils/icon.helper";
import { ITextButton } from "@/components/atoms/buttons/types";
// ------------------------------------------------------------------------------------------
export type ISecurityCard = {
  heading?: string;
  description?: ReactNode;
  icon?: IIconName;
};
export type ITakeSecurityAssessmentProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  description?: ReactNode;
  securityCards?: ISecurityCard[];
  button?: ITextButton;
};
const TakeSecurityAssessment: FC<ITakeSecurityAssessmentProps> = (props) => {
  const { heading, description, securityCards, button, className, ...other } =
    props;
  return (
    <section
      className={clsx("container py-5 lg:py-8 xl:py-[50px]", className)}
      {...other}
    >
      <div className="mb-5 w-full max-w-[789px] lg:mb-10">
        {heading && (
          <h2 className="mb-4 text-3xl font-medium text-black-900 lg:mb-7 lg:text-h2">
            {heading}
          </h2>
        )}
        {description && <HtmlText>{description}</HtmlText>}
      </div>
      {securityCards && securityCards.length > 0 && (
        <div className="flex flex-col flex-wrap gap-5 md:flex-row lg:gap-7">
          {securityCards.map((card, idx) => (
            <div
              key={idx}
              className="group relative flex min-h-48 flex-1 flex-col justify-center overflow-hidden bg-primary-100 p-7 transition-all duration-[400ms] ease-in-out ease-linear hover:flex-auto md:h-[204px] md:min-w-60 md:max-w-[50%] md:hover:max-w-none md:hover:flex-[1_1_406px] lg:h-[260px] lg:min-w-52 lg:p-7 xl:w-[380px] xl:min-w-[285px] xl:px-11 xl:py-12 xl:hover:px-12 xl:hover:py-12"
            >
              <div className="absoulte duration-400 opacity-100 transition-all group-hover:opacity-0">
                <div>
                  <div className="mb-5 size-8 lg:mb-7 lg:size-10 xl:size-[50px]">
                    {card.icon && (
                      <Image
                        className="size-full"
                        src={icon(card.icon)}
                        alt={card.icon}
                        width={50}
                        height={50}
                      />
                    )}
                  </div>
                  {card.heading && (
                    <h5 className="text-wrap text-h6 font-normal text-black-900 lg:text-h5">
                      {card.heading}
                    </h5>
                  )}
                </div>
              </div>
              <div className="duration-600 absolute left-5 right-5 opacity-0 transition-all group-hover:opacity-100 md:left-12 md:right-12 md:w-[316px] xl:w-[360px] 2xl:w-[478px]">
                <div className="mb-5 flex items-center lg:mb-7">
                  {card.icon && (
                    <Image
                      className="mr-5 size-9 lg:size-[50px]"
                      src={icon(card.icon)}
                      alt={card.icon}
                      width={50}
                      height={50}
                    />
                  )}
                  {card.heading && (
                    <h5 className="text-wrap text-h6 font-normal text-black-900 lg:text-h5">
                      {card.heading}
                    </h5>
                  )}
                </div>
                {card.description && (
                  <div>
                    <HtmlText className="line-clamp-4 text-base font-normal text-gray-500 lg:text-h6">
                      {card.description}
                    </HtmlText>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-7 flex justify-center md:mt-12">
        {button && (
          <TButton
            id={button.id}
            href={button.link}
            target={button.target}
            variant={button.variant}
            radius={button.radius}
          >
            {button.text}
          </TButton>
        )}
      </div>
    </section>
  );
};
export default TakeSecurityAssessment;
